"use client";

import React, { useState } from "react";

import { Button } from "@heroui/button";
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
} from "@heroui/drawer";

import { NeedsFilter } from "@/components/main/needs/NeedsFilter";
import { OrganizationTerminology } from "@/lib/services/terminologyService";

interface FilterDrawerProps {
  areas: {
    id: string;
    slug: string;
    name: string;
    state: string;
  }[];
  categories: {
    id: string;
    name: string;
  }[];
  initialFilters: {
    searchQuery: string;
    selectedAreas: string[];
    selectedCategory: string;
    selectedUrgency: string[];
  };
  orgSlug: string;
  terminology: OrganizationTerminology;
}

const FilterIcon = () => (
  <svg
    aria-hidden="true"
    className="w-5 h-5"
    fill="none"
    focusable="false"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
    />
  </svg>
);

export function FilterDrawer({
  areas,
  categories,
  initialFilters,
  orgSlug,
  terminology,
}: FilterDrawerProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Calculate active filter count for badge
  const activeFiltersCount =
    (initialFilters.searchQuery ? 1 : 0) +
    initialFilters.selectedAreas.length +
    initialFilters.selectedUrgency.length +
    (initialFilters.selectedCategory ? 1 : 0);

  const filterContent = (
    <NeedsFilter
      areas={areas}
      categories={categories}
      initialFilters={initialFilters}
      orgSlug={orgSlug}
      terminology={terminology}
    />
  );

  return (
    <>
      {/* Mobile: Filter Button */}
      <div className="md:hidden mb-4">
        <Button
          aria-label="Open filters"
          className="w-full"
          color="primary"
          data-testid="open-filters-button"
          startContent={<FilterIcon />}
          variant="bordered"
          onPress={() => setIsOpen(true)}
        >
          Filters
          {activeFiltersCount > 0 && (
            <span className="ml-2 px-2 py-0.5 bg-primary text-primary-foreground rounded-full text-xs font-semibold">
              {activeFiltersCount}
            </span>
          )}
        </Button>
      </div>

      {/* Mobile: Drawer with Filters */}
      <Drawer
        isOpen={isOpen}
        placement="left"
        size="md"
        onClose={() => setIsOpen(false)}
      >
        <DrawerContent>
          <DrawerHeader>
            <h2 className="text-lg font-semibold">
              Filter {terminology.need.plural.toLowerCase()}
            </h2>
          </DrawerHeader>
          <DrawerBody className="p-0">
            <div className="p-4">{filterContent}</div>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Desktop: Sidebar */}
      <div
        aria-labelledby="filter-heading"
        className="hidden md:block w-full md:w-60 shrink-0 border-r border-divider"
        role="search"
      >
        <h2 className="sr-only" id="filter-heading">
          Filter {terminology.need.plural.toLowerCase()}
        </h2>
        {filterContent}
      </div>
    </>
  );
}
